// Service configuration card: mints and reveals a location's posting-service
// credential (POST /v1/locations/{id}/service-config). Modelled on
// display-url-card.tsx — the existing action-card pattern here: a Card, a
// Button, CopyButtons, and toast on failure.
//
// The minted artifacts live only in this component's local state, never in
// the query cache: the server shows the key exactly once and doesn't re-serve
// it, so nothing here may be prefetched or persisted. They stay on screen
// after the mutation settles purely so the operator has time to copy them.
//
// Deliberately outside the location's <Form>: this is an independent
// mutation, not a form field, and must never dirty the save bar or wait on
// its Save.

import { Button, Card, CopyButton } from "@pej/kore/components";
import { useState } from "react";
import { ApiError } from "@/api/error";
import { useServiceConfig } from "@/api/locations";
import type { Location, ServiceConfigDocument } from "@/api/types";
import { toastMutationError } from "@/components/common/toast-mutation-error";
import { saveFile } from "@/lib/save-file";

export interface ServiceConfigCardProps {
    workspaceId: string;
    // Bound-subject status is configure-sections.tsx's "Service account" row;
    // this card only needs the id to mint against.
    location: Pick<Location, "id">;
}

export function ServiceConfigCard({ workspaceId, location }: ServiceConfigCardProps) {
    const serviceConfig = useServiceConfig(workspaceId, location.id);
    const [revealed, setRevealed] = useState<ServiceConfigDocument | null>(null);

    async function handleDownload() {
        try {
            const result = await serviceConfig.mutateAsync();
            setRevealed(result);
            // The comment above claims the document lives only in this
            // component's local state — true only once the mutation's own
            // cache entry (mutation.data) is cleared too.
            serviceConfig.reset();
        } catch (err) {
            if (!(err instanceof ApiError)) throw err;
            toastMutationError("Download failed", err);
        }
    }

    return (
        <Card className="p-6">
            <h2 className="mb-4 text-base font-semibold">Service account</h2>
            <div className="mt-4 flex items-center gap-3">
                <Button onClick={() => void handleDownload()} disabled={serviceConfig.isPending}>
                    {serviceConfig.isPending ? "Downloading…" : "Download configuration"}
                </Button>
            </div>
            {revealed?.keyJson ? (
                <div className="mt-4 flex flex-col gap-4">
                    <p className="text-sm text-kore-text-muted">
                        Save these two files in the same folder as the posting service.
                    </p>
                    <ConfigArtifact filename="config.toml" content={revealed.configToml} />
                    <ConfigArtifact filename="key.json" content={revealed.keyJson} />
                    <p className="text-sm text-kore-text-muted">
                        The key is shown once — save it now. A replacement key can't be issued yet.
                    </p>
                </div>
            ) : revealed ? (
                <div className="mt-4 flex flex-col gap-4">
                    <ConfigArtifact filename="config.toml" content={revealed.configToml} />
                    <p className="text-sm text-kore-text-muted">
                        This location already has a service account. Its key was shown once when it was first set up —
                        re-issuing a key is coming soon.
                    </p>
                </div>
            ) : null}
        </Card>
    );
}

function ConfigArtifact({ filename, content }: { filename: string; content: string }) {
    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{filename}</span>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => saveFile(filename, content)}>
                        Save {filename}
                    </Button>
                    <CopyButton value={content} label={`Copy ${filename}`} size="sm" />
                </div>
            </div>
            <code className="block overflow-x-auto whitespace-pre rounded-kore-md border border-kore-border-soft bg-kore-surface-soft px-3 py-2 text-xs">
                {content}
            </code>
        </div>
    );
}
